/** @jsxImportSource ../../i18n */
import React, { forwardRef } from 'react';
import { cx } from '../../theme/cx';
import { Spinner } from './Feedback';
import { Tooltip } from './Tooltip';

const VARIANTS = {
  primary: 'bg-gold-500 font-semibold text-on-gold shadow-[inset_0_1px_0_rgb(255_255_255/0.22)] hover:bg-gold-400 active:bg-gold-600',
  secondary: 'border border-line-strong bg-surface-2 font-medium text-fg hover:border-fg-faint hover:bg-surface-3',
  ghost: 'font-medium text-fg-muted hover:bg-surface-2 hover:text-fg',
  danger: 'border border-loss-500/30 bg-loss-900/60 font-medium text-loss-300 hover:border-loss-500/50 hover:bg-loss-900',
  link: 'font-medium text-gold-300 underline-offset-4 hover:underline',
};

const SIZES = {
  sm: 'h-8 gap-1.5 px-3 text-[13px]',
  md: 'h-10 gap-2 px-4 text-sm',
  lg: 'h-12 gap-2.5 px-6 text-[15px]',
};

const ICON_SIZES = { sm: 15, md: 16, lg: 18 };

export const Button = forwardRef(({
  as: Component = 'button',
  variant = 'primary',
  size = 'md',
  loading = false,
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  fullWidth = false,
  className,
  children,
  disabled,
  type,
  ...rest
}, ref) => {
  const native = Component === 'button';
  const iconSize = ICON_SIZES[size] || ICON_SIZES.md;
  return (
    <Component
      ref={ref}
      type={native ? type || 'button' : undefined}
      disabled={native ? disabled || loading : undefined}
      aria-disabled={!native && disabled ? true : undefined}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex select-none items-center justify-center whitespace-nowrap rounded-control transition-[background-color,border-color,color,transform] duration-150 ease-standard active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45',
        VARIANTS[variant] || VARIANTS.primary,
        variant === 'link' ? 'gap-1.5 text-sm' : SIZES[size] || SIZES.md,
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={iconSize} tone="current" /> : LeadingIcon && <LeadingIcon size={iconSize} aria-hidden="true" />}
      {children}
      {!loading && TrailingIcon && <TrailingIcon size={iconSize} aria-hidden="true" />}
    </Component>
  );
});

const ICON_BUTTON_SIZES = { sm: ['h-8 w-8', 15], md: ['h-10 w-10', 17], lg: ['h-12 w-12', 19] };

const ICON_BUTTON_VARIANTS = {
  ghost: 'text-fg-muted hover:bg-surface-2 hover:text-fg',
  secondary: 'border border-line-strong bg-surface-2 text-fg-body hover:bg-surface-3 hover:text-fg',
  danger: 'text-fg-muted hover:bg-loss-900/50 hover:text-loss-300',
  primary: 'bg-gold-500 text-on-gold shadow-[inset_0_1px_0_rgb(255_255_255/0.22)] hover:bg-gold-400 active:bg-gold-600',
};

export const IconButton = forwardRef(({
  icon: Icon,
  label,
  variant = 'ghost',
  size = 'md',
  pressed,
  tooltip = true,
  tooltipSide = 'bottom',
  className,
  iconClassName,
  type = 'button',
  children,
  ...rest
}, ref) => {
  const [sizeClass, iconSize] = ICON_BUTTON_SIZES[size] || ICON_BUTTON_SIZES.md;
  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      aria-pressed={pressed}
      className={cx(
        'inline-flex flex-shrink-0 items-center justify-center rounded-control transition-[background-color,border-color,color,transform] duration-150 ease-standard active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40',
        ICON_BUTTON_VARIANTS[variant] || ICON_BUTTON_VARIANTS.ghost,
        pressed && 'bg-surface-2 text-gold-300',
        sizeClass,
        className,
      )}
      {...rest}
    >
      {Icon && <Icon size={iconSize} aria-hidden="true" className={iconClassName} />}
      {children}
    </button>
  );
  return tooltip ? <Tooltip content={label} side={tooltipSide}>{button}</Tooltip> : button;
});
